<template>
  <div :id="componentId+'-plot_area'">
  </div>
</template>

<script>
import * as d3 from "d3";
import {loadingCircle} from "../mixins/D3Mixin";

export default {
  props: ["plotData", "componentId"],
  name: "TreemapWidget",
  mixins: [loadingCircle],
  data() {
    return {
      divId : this.componentId + '-plot_area'
    }
  },

  watch: {
    plotData() {
      this.drawTreemap()
    }
  },

  mounted() {
    this.initSvg()
  },

  methods: {

    initSvg() {
      this.divElement = document.getElementById(this.divId);
      this.drawTreemap()
    },
    drawTreemap() {
      if (this.divElement.childNodes.length > 0) {
        while (this.divElement.firstChild) {
          this.divElement.removeChild(this.divElement.lastChild);
        }
      }
      let margin = {top: 20, right: 20, bottom: 30, left: 40},
          width = 350 - margin.left - margin.right,
          height = 198 - margin.top - margin.bottom;
      if (this.plotData == null) {
        this.spinningCircle(margin, width, height)
      }
      else {
        // eslint-disable-next-line no-prototype-builtins
        if (this.plotData.hasOwnProperty('children') && this.plotData.children.length >= 1) {
          // let height = 400;
          // let width = 500;
          let format = d3.format("");
          let uids = new Map();
          let uid = title => {
            let counter = uids.has(title) ? uids.get(title) + 1 : 0;
            uids.set(title, counter);
            return `${title}-${counter}`;
          }
          let name = d => d.ancestors().reverse().map(d => d.data.name).join("/");

          // eslint-disable-next-line no-inner-declarations
          function tile(node, x0, y0, x1, y1) {
            d3.treemapBinary(node, 0, 0, width, height);
            for (const child of node.children) {
              child.x0 = x0 + child.x0 / width * (x1 - x0);
              child.x1 = x0 + child.x1 / width * (x1 - x0);
              child.y0 = y0 + child.y0 / height * (y1 - y0);
              child.y1 = y0 + child.y1 / height * (y1 - y0);
            }
          }

          let treemap = data => d3.treemap()
              .tile(tile)(d3.hierarchy(data)
                  .sum(d => d.value)
                  .sort((a, b) => b.value - a.value))


          const x = d3.scaleLinear().rangeRound([0, width]);
          const y = d3.scaleLinear().rangeRound([0, height]);

          const svg = d3.select(this.divElement)
              .append("svg")
              .attr("viewBox", [0.5, -30.5, width, height + 30])
              .style("font", "8px sans-serif");

          let group = svg.append("g")
              .call(render, treemap(this.plotData));

          // eslint-disable-next-line no-inner-declarations
          function render(group, root) {


            const node = group
                .selectAll("g")
                .data(root.children.concat(root))
                .data(root.children.concat(root))
                .join("g");

            node.filter(d => d === root ? d.parent : d.children)
                .attr("cursor", "pointer")
                .on("click", (event, d) => d === root ? zoomout(root) : zoomin(d));

            node.append("title")
                .text(d => `${name(d)}\n${format(d.value)}`)
                .style("font-size", "70%");


            node.append("rect")
                .attr("id", d => (d.leafUid = uid("leaf")))
                .attr("fill", d => d === root ? "#edf3f7" : d.children ? "#0b9ba3" : "#0b9ba3")
                .attr("stroke", "#edf3f7");

            node.append("clipPath")
                .attr("id", d => (d.clipUid = uid("clip")))
                .append("use")
                .attr("xlink:href", d => d.leafUid.href);

            node.append("text")
                .attr("clip-path", d => d.clipUid)
                .attr("font-weight", d => d === root ? "bold" : null)
                .selectAll("tspan")
                .data(d => (d.data.name).split(/(?=[A-Z][^A-Z])/g).concat(format(d.value)))
                .join("tspan")
                .attr("x", 3)
                .attr("y", (d, i, nodes) => `${(i === nodes.length - 1) * 0.3 + 1.1 + i * 0.9}em`)
                .attr("fill-opacity", (d, i, nodes) => i === nodes.length - 1 ? 0.7 : null)
                .attr("font-weight", (d, i, nodes) => i === nodes.length - 1 ? "normal" : null)
                .text(d => d);

            group.call(position, root);
          }

          // eslint-disable-next-line no-inner-declarations
          function position(group, root) {
            group.selectAll("g")
                .attr("transform", d => d === root ? `translate(0,-30)` : `translate(${x(d.x0)},${y(d.y0)})`)
                .select("rect")
                .attr("width", d => d === root ? width : x(d.x1) - x(d.x0))
                .attr("height", d => d === root ? 30 : y(d.y1) - y(d.y0));
          }

          // When zooming in, draw the new nodes on top, and fade them in.
          // eslint-disable-next-line no-inner-declarations
          function zoomin(d) {
            const group0 = group.attr("pointer-events", "none");
            const group1 = group = svg.append("g").call(render, d);

            x.domain([d.x0, d.x1]);
            y.domain([d.y0, d.y1]);

            svg.transition()
                .duration(750)
                .call(t => group0.transition(t).remove()
                    .call(position, d.parent))
                .call(t => group1.transition(t)
                    .attrTween("opacity", () => d3.interpolate(0, 1))
                    .call(position, d));
          }

          // When zooming out, draw the old nodes on top, and fade them out.
          // eslint-disable-next-line no-inner-declarations
          function zoomout(d) {
            const group0 = group.attr("pointer-events", "none");
            const group1 = group = svg.insert("g", "*").call(render, d.parent);

            x.domain([d.parent.x0, d.parent.x1]);
            y.domain([d.parent.y0, d.parent.y1]);

            svg.transition()
                .duration(750)
                .call(t => group0.transition(t).remove()
                    .attrTween("opacity", () => d3.interpolate(1, 0))
                    .call(position, d))
                .call(t => group1.transition(t)
                    .call(position, d.parent));
          }
        }
        else {
          this.notEnoughData(margin, width, height)
        }
      }
    }
  }
}
</script>

<style scoped>

</style>